import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';
import { StyledScrollContainer } from '../../../components/StyledScrollContainer';
import { DialogVehicleDeliveryConfirmation } from '../../../components/DialogVehicleDeliveryConfirmation';

// @components
import {
    Avatar,
    Button,
    Typography,
    Card,
} from "@material-tailwind/react";

import {
    Vehicle,
    listVehiclesDeliveryRequest,
} from "../../../redux/vehicles/actions";
import AppImages from '../../../assets/AppImages';
import { RootState } from '../../../redux';


const TABLE_HEAD = [
    {n: "Name", className: "w-30"},
    {n: "VIN", className: "w-30"},
    {n: "ETA", className: "w-30 text-center"},
    {n: "", className: "w-20"},
];

export function DashboardVehicleDeliveryWeek() {
    const dispatch = useDispatch();
    const navigation = useNavigate();
    const { deliveries } = useSelector((state: RootState) => state.vehicles);
    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        const date_range = getExpectedDateRange('YYYY-MM-DD').split(' - ');
        dispatch(
            listVehiclesDeliveryRequest({
                date_from: date_range[0],
                date_to: date_range[1],
                vehicle_fields: [
                    "id",
                    "status",
                    "display_status",
                    "is_listed",
                    "ymmt",
                    "thumbnail",
                    "make",
                    "model",
                    "vin",
                    "inspection_score",
                    "inspection_results",
                    "parts",
                    "tasks",
                    "labors",
                    "delivery_company",
                    "delivery_order",
                    "delivery_pickup_date",
                    "delivery_est_date",
                    "delivery_date",
                ].join(',')
            })
        );
    }, [dispatch]);

    const getExpectedDateRange = (format = 'MMM D'): string => {
        const now = Moment();

        const startOfWeek = now.clone().startOf('week').add(1, 'day'); // Adjust for Monday as the first day
        const endOfWeek = now.clone().endOf('week').add(1, 'day'); // Sunday remains the last day

        const formattedStartDate = startOfWeek.format(format); // e.g., "Nov 14"
        const formattedEndDate = endOfWeek.format(format); // e.g., "Nov 20"

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    return (
        <Card>
            <Card.Header className="w-full m-0 items-center rounded-none flex  justify-between p-4">
                <div className="grow">
                    <Typography
                        type="lead"
                        color="default"
                        className="font-semibold mb-1"
                    >
                        Vehicles Deliveries
                    </Typography>
                    <Typography type="small" className="text-foreground block">
                        All vehicle deliveries expected on {getExpectedDateRange()}
                    </Typography>
                </div>
                <div className="flex flex-nowrap">
                    <Button
                        variant="outline"
                        color="secondary"
                        onClick={() => navigation('/dashboard/vehicles/deliveries')}
                        className="flex items-center shrink-0 gap-2 sm:w-auto w-full"
                    >
                        See All
                    </Button>
                </div>
            </Card.Header>
            <StyledScrollContainer className="max-h-[400px]">
                <Card.Body className="p-0 rounded-none">
                    <table className="w-full min-w-max table-auto text-left">
                        <thead>
                            <tr>
                                {TABLE_HEAD.map((head, idx:number) => (
                                    <th key={idx} className={`border-b border-surface p-4 ${head.className}`}>
                                        <Typography
                                            type="small"
                                            color="default"
                                            className="block font-medium"
                                        >
                                            {head.n}
                                        </Typography>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {deliveries.map((el: Vehicle, key: number) => (
                                <tr key={key} className="group">
                                    <td className="p-4 border-b border-surface group-last:border-none">
                                        <div className="flex items-center gap-3">
                                            <Avatar src={el?.thumbnail?.full_url} alt={el?.ymmt} shape="rounded" />
                                            <div className="space-y-1">
                                                <Typography
                                                    type="small"
                                                    color="default"
                                                    className="font-semibold block overflow-hidden w-20 text-ellipsis text-nowrap"
                                                >
                                                    {el?.ymmt || 'N/A'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-surface group-last:border-none">
                                        <Typography type="small" className="block text-foreground w-30 overflow-hidden text-ellipsis text-nowrap">
                                            {el.vin}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface group-last:border-none">
                                        <Typography type="small" className="text-center block text-foreground">
                                            {el.delivery_est_date || 'N/A'}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface group-last:border-none">
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            color="secondary"
                                            className="border-[#E2E8F0]"
                                            onClick={() => {
                                                setSelectedVehicle(el);
                                                setOpenDialog(true);
                                            }}
                                        >
                                            Delivered
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {deliveries.length == 0 &&
                        <div className="flex flex-col my-20 gap-5 justify-center items-center h-full">
                            <div className="flex bg-secondary items-center p-4 rounded-full">
                                <AppImages.IconVehicles className="w-6 h-6" />
                            </div>
                            <Typography className="text-primary font-semibold text-2xl">
                                No expected vehicle deliveries
                            </Typography>
                        </div>
                    }
                </Card.Body>
            </StyledScrollContainer>

            <DialogVehicleDeliveryConfirmation
                vehicle={selectedVehicle}
                setIsOpen={setOpenDialog}
                isOpen={openDialog}
            />

        </Card>
    );
}
